// -----------------------------------------------------------
// FloraIQ - Memorial Descritivo da Plataforma (Mock Data)
// -----------------------------------------------------------

export const memorialOverview = {
  titulo: "FloraIQ — Sistema de Inteligência Ambiental Urbana",
  versao: "v1.0.0",
  dataAtualizacao: "01/07/2026",
  responsavel: "Equipe Técnica FloraIQ",
  organizacao: "Instituto de Pesquisa e Desenvolvimento FloraIQ",
  descricao: `O FloraIQ é uma plataforma SaaS institucional de inteligência ambiental urbana, desenvolvida para 
organizar, estruturar e governar o Cadastro Técnico Arbóreo (CTA) de municípios, permitindo a gestão 
integrada de inventários arbóreos, áreas verdes, compensação ambiental, projetos de carbono urbano 
e governança territorial baseada em dados geoespaciais de alta precisão.`,
  objetivoGeral: `Proporcionar uma base territorial inteligente, auditável e integrada à gestão municipal, 
permitindo rastrear quem informou, quando informou, qual fonte comprova e qual responsabilidade 
está vinculada a cada árvore cadastrada no sistema.`,
  escopo: `O sistema abrange o ciclo completo de gestão da arborização urbana: planejamento, levantamento 
geoespacial, cadastro de campo, processamento 3D, modelagem cadastral conforme metodologia LADM-FloraIQ, 
validação técnica, publicação de produtos e gestão contínua com indicadores de desempenho.`,
};

export const memorialArchitecture = [
  {
    camada: "Frontend",
    descricao: "Interface web responsiva construída em React 19 + TypeScript + TailwindCSS, com componentização modular e design system próprio baseado em tokens de cor e tipografia.",
    tecnologias: ["React 19", "TypeScript", "TailwindCSS", "Vite", "React Router DOM"],
    caracteristicas: ["SPA — Single Page Application", "Design responsivo (desktop-first)", "Componentes reutilizáveis", "Animações CSS suaves", "Acessibilidade (ARIA, contraste, navegação por teclado)"],
  },
  {
    camada: "Backend",
    descricao: "API RESTful desenvolvida em Python com FastAPI, oferecendo alto desempenho, validação automática de schemas via Pydantic e documentação OpenAPI nativa.",
    tecnologias: ["Python 3.12", "FastAPI", "Pydantic", "Uvicorn", "Celery (tarefas assíncronas)"],
    caracteristicas: ["RESTful API", "Async/await nativo", "Validação schema-first", "Documentação auto-gerada", "Workers para processamento pesado"],
  },
  {
    camada: "Banco de Dados",
    descricao: "PostgreSQL com extensão PostGIS para armazenamento e consulta espacial. Arquitetura relacional normalizada com suporte a dados geoespaciais, histórico de versionamento e auditoria.",
    tecnologias: ["PostgreSQL 16", "PostGIS 3.4", "pgAdmin", "Migrations versionadas"],
    caracteristicas: ["Dados geoespaciais nativos", "SRID configurável por município", "Índices espaciais GIST", "Backup incremental", "Replicação para leitura"],
  },
  {
    camada: "Processamento 3D",
    descricao: "Pipeline de processamento de nuvens de pontos, ortomosaicos e modelos 3D provenientes de drones, LiDAR e laser scanner terrestre.",
    tecnologias: ["PDAL", "Potree", "CesiumJS", "Three.js", "Open3D", "LAStools"],
    caracteristicas: ["Import LAS/LAZ/PLY", "Visualização web 3D", "Extração métricas arborícolas", "Classificação semântica", "Comparação temporal"],
  },
  {
    camada: "GIS e Cartografia",
    descricao: "Visualização e análise geoespacial integrada com mapas interativos, camadas vetoriais, raster e análises espaciais.",
    tecnologias: ["Leaflet", "MapLibre GL", "GeoJSON", "Shapefile", "GeoTIFF", "WMS/WFS"],
    caracteristicas: ["Mapas interativos", "Múltiplas camadas temáticas", "Busca espacial", "Export GeoJSON/Shapefile", "Medição e desenho"],
  },
  {
    camada: "Armazenamento e Assets",
    descricao: "Storage para fotos, documentos, nuvens de pontos, ortomosaicos e modelos 3D com controle de versão e metadados.",
    tecnologias: ["AWS S3 / MinIO", "CDN para imagens", "Compressão automática", "Metadados EXIF"],
    caracteristicas: ["URL pré-assinada", "Thumbnails automáticos", "Versionamento", "Metadados técnicos"],
  },
  {
    camada: "Segurança e Autenticação",
    descricao: "Sistema de autenticação com JWT, controle de acesso baseado em papéis (RBAC), auditoria de operações e criptografia de dados sensíveis.",
    tecnologias: ["JWT", "RBAC", "bcrypt", "HTTPS/TLS 1.3", "Rate limiting"],
    caracteristicas: ["Autenticação JWT", "Permissões granulares", "Logs de auditoria", "Criptografia em repouso", "2FA opcional"],
  },
];

export const memorialModules = [
  {
    id: "dashboard",
    nome: "Dashboard",
    icone: "ri-dashboard-line",
    descricao: "Painel central com indicadores operacionais, alertas prioritários, mapa resumo, projetos recentes, espécies mais comuns e status das vistorias.",
    funcionalidades: [
      "KPIs operacionais em tempo real",
      "Gráficos de status e espécies",
      "Mapa operacional com filtros",
      "Alertas prioritários por risco",
      "Projetos recentes e atividades",
    ],
    publicoAlvo: "Gestores municipais, coordenadores técnicos, diretores ambientais",
  },
  {
    id: "projetos",
    nome: "Projetos",
    icone: "ri-folder-chart-line",
    descricao: "Gestão de projetos de cadastramento arbóreo com cronograma, equipe, orçamento, escopo, indicadores de progresso e árvores vinculadas.",
    funcionalidades: [
      "Cadastro de projetos com metadados",
      "Cronograma e marcos",
      "Equipe e responsáveis",
      "Progresso por etapa",
      "Relatórios por projeto",
    ],
    publicoAlvo: "Coordenadores de projeto, gestores de contrato, prefeituras",
  },
  {
    id: "cta",
    nome: "CTA Arbóreo",
    icone: "ri-plant-line",
    descricao: "Cadastro Técnico Arbóreo com ficha completa por árvore: dados botânicos, dendrométricos, localização georreferenciada, fotos, vistorias, interferências e manejo.",
    funcionalidades: [
      "Ficha cadastral completa (FCA)",
      "Georreferenciamento preciso",
      "Fotos e evidências vinculadas",
      "Classificação de risco",
      "Recomendação de manejo",
    ],
    publicoAlvo: "Técnicos de campo, biólogos, engenheiros florestais, agrônomos",
  },
  {
    id: "mapa",
    nome: "Mapa Arbóreo",
    icone: "ri-map-pin-line",
    descricao: "Visualização cartográfica interativa de todas as árvores cadastradas, com camadas temáticas, ferramentas de medição, filtros espaciais e exportação GIS.",
    funcionalidades: [
      "Mapa interativo com popups",
      "Camadas temáticas (risco, espécie, manejo)",
      "Ferramentas de medição e desenho",
      "Busca espacial por raio",
      "Exportação GeoJSON/Shapefile",
    ],
    publicoAlvo: "Analistas GIS, cartógrafos, técnicos ambientais",
  },
  {
    id: "vistorias",
    nome: "Vistorias",
    icone: "ri-clipboard-line",
    descricao: "Acompanhamento de vistorias técnicas programadas e realizadas, com checklists, fotos, pareceres, ciclos de monitoramento e comparação temporal.",
    funcionalidades: [
      "Agendamento de vistorias",
      "Checklists técnicos",
      "Fotos e evidências",
      "Ciclos de monitoramento",
      "Comparação temporal",
    ],
    publicoAlvo: "Inspetores técnicos, engenheiros, consultores ambientais",
  },
  {
    id: "ambiental",
    nome: "Ambiental",
    icone: "ri-landscape-line",
    descricao: "Gestão de áreas verdes, praças, parques e Unidades de Conservação (UCs) com inventário de vegetação, espécies nativas, manejo e monitoramento.",
    funcionalidades: [
      "Cadastro de áreas verdes",
      "Inventário de UCs",
      "Espécies nativas e exóticas",
      "Plano de manejo ambiental",
      "Indicadores de cobertura vegetal",
    ],
    publicoAlvo: "Gestores ambientais, biólogos, ecólogos, órgãos ambientais",
  },
  {
    id: "carbono",
    nome: "Carbono",
    icone: "ri-leaf-line",
    descricao: "Estimativa de sequestro de carbono por árvore e por área, com modelos alométricos, projeções temporais, compensação ambiental e projetos de crédito de carbono.",
    funcionalidades: [
      "Estimativa de biomassa e carbono",
      "Modelos alométricos por espécie",
      "Projeções temporais",
      "Compensação ambiental",
      "Relatórios de crédito de carbono",
    ],
    publicoAlvo: "Consultores de carbono, engenheiros ambientais, empresas compensadoras",
  },
  {
    id: "documentos",
    nome: "Documentos e Fontes",
    icone: "ri-file-list-3-line",
    descricao: "Repositório de documentos técnicos, administrativos e legais vinculados a árvores, projetos e áreas, com rastreabilidade, validade e evidências.",
    funcionalidades: [
      "Upload de documentos",
      "Vinculação a árvores e projetos",
      "Controle de validade",
      "Categorização por tipo",
      "Busca e filtros avançados",
    ],
    publicoAlvo: "Administradores, jurídico, técnicos documentalistas",
  },
  {
    id: "partes",
    nome: "Partes Envolvidas",
    icone: "ri-team-line",
    descricao: "Cadastro de pessoas físicas, jurídicas, instituições, órgãos públicos e empresas envolvidas na gestão arbórea, com papéis, responsabilidades e vínculos.",
    funcionalidades: [
      "Cadastro de partes",
      "Vinculação a árvores e projetos",
      "Papéis e responsabilidades",
      "Autorizações e permissões",
      "Histórico de vínculos",
    ],
    publicoAlvo: "Administradores, jurídico, gestores de relacionamento",
  },
  {
    id: "responsabilidades",
    nome: "Responsabilidades",
    icone: "ri-shield-check-line",
    descricao: "Registro de Responsabilidades, Restrições e Direitos (RRR) sobre árvores e áreas, incluindo propriedade, uso, manutenção, supressão e compensação.",
    funcionalidades: [
      "Cadastro de RRR",
      "Vinculação espacial",
      "Tipos de responsabilidade",
      "Restrições ambientais",
      "Autorizações de supressão",
    ],
    publicoAlvo: "Jurídico, gestores territoriais, técnicos de licenciamento",
  },
  {
    id: "metodologia",
    nome: "Metodologia LADM",
    icone: "ri-node-tree",
    descricao: "Documentação e aplicação da metodologia FloraIQ-LADM para estruturação dos dados cadastrais segundo o padrão internacional ISO 19152.",
    funcionalidades: [
      "Matriz conceitual LADM",
      "Fluxo metodológico",
      "Vínculos territoriais",
      "Rastreabilidade de fontes",
      "Histórico e versionamento",
    ],
    publicoAlvo: "Pesquisadores, cadastristas, técnicos de SIG, gestores territoriais",
  },
  {
    id: "nuvem3d",
    nome: "Nuvem de Pontos 3D",
    icone: "ri-stack-line",
    descricao: "Gestão de levantamentos 3D com drone, LiDAR e laser scanner, com visualizador, classificação de pontos, extração de métricas e vinculação a árvores.",
    funcionalidades: [
      "Import LAS/LAZ/PLY",
      "Visualizador 3D web",
      "Classificação semântica",
      "Extração de métricas arborícolas",
      "Comparação temporal",
    ],
    publicoAlvo: "Topógrafos, especialistas em sensoriamento remoto, pesquisadores",
  },
  {
    id: "qualidade",
    nome: "Validação e Qualidade",
    icone: "ri-check-double-line",
    descricao: "Motor de validação automática com regras técnicas, GIS, documentais e metodológicas para garantir consistência e conformidade dos dados.",
    funcionalidades: [
      "32 regras de validação",
      "Validação GIS",
      "Validação arbórea",
      "Validação documental",
      "Correções assistidas",
    ],
    publicoAlvo: "Analistas de qualidade, revisores técnicos, gestores de dados",
  },
  {
    id: "operacional",
    nome: "Fluxo Operacional",
    icone: "ri-git-branch-line",
    descricao: "Acompanhamento ponta a ponta das ações de cadastramento, com 7 etapas operacionais, checklists, pendências, validações e geração de produtos.",
    funcionalidades: [
      "7 etapas operacionais",
      "Checklists interativos",
      "Controle de pendências",
      "Validação por etapa",
      "Relatórios operacionais",
    ],
    publicoAlvo: "Coordenadores operacionais, gestores de projeto, equipe de campo",
  },
  {
    id: "relatorios",
    nome: "Relatórios",
    icone: "ri-bar-chart-box-line",
    descricao: "Geração de relatórios técnicos, executivos, fotográficos, cartográficos e ambientais com assistente de geração, modelos e exportação.",
    funcionalidades: [
      "Assistente de geração (6 etapas)",
      "Modelos customizáveis",
      "Relatórios por módulo",
      "Exportação PDF/Excel/GIS",
      "Agendamento de relatórios",
    ],
    publicoAlvo: "Técnicos, gestores, prefeituras, consultores, pesquisadores",
  },
  {
    id: "configuracoes",
    nome: "Configurações",
    icone: "ri-settings-3-line",
    descricao: "Gerenciamento de parâmetros técnicos, espécies, critérios de risco, usuários, permissões, integrações e auditoria do sistema.",
    funcionalidades: [
      "Cadastro de espécies",
      "Critérios de risco",
      "Gestão de usuários e permissões",
      "Integrações e APIs",
      "Logs de auditoria",
    ],
    publicoAlvo: "Administradores do sistema, gestores de TI, coordenadores técnicos",
  },
  {
    id: "coletor",
    nome: "Coletor de Campo",
    icone: "ri-smartphone-line",
    descricao: "Aplicação PWA para coleta de dados em campo offline, com formulários estruturados, fotos georreferenciadas, GPS/GNSS e sincronização.",
    funcionalidades: [
      "Funcionamento offline",
      "Formulários estruturados",
      "Fotos georreferenciadas",
      "GPS/GNSS integrado",
      "Sincronização automática",
    ],
    publicoAlvo: "Técnicos de campo, estagiários, voluntários",
  },
  {
    id: "publico",
    nome: "Portal Público",
    icone: "ri-earth-line",
    descricao: "Portal de transparência para cidadãos consultarem dados públicos da arborização, áreas verdes, projetos e participarem da gestão urbana.",
    funcionalidades: [
      "Consulta pública de árvores",
      "Mapa interativo aberto",
      "Indicadores municipais",
      "Participação cidadã",
      "Denúncias e solicitações",
    ],
    publicoAlvo: "Cidadãos, estudantes, jornalistas, pesquisadores, ONGs",
  },
];

export const memorialMethodology = {
  titulo: "Metodologia FloraIQ-LADM",
  descricao: `A metodologia FloraIQ-LADM adapta o padrão internacional ISO 19152 (Land Administration Domain Model — LADM) 
para o domínio da arborização urbana, criando uma estrutura cadastral que relaciona árvores, unidades espaciais, 
fontes, responsáveis, restrições, eventos e histórico de maneira rastreável e interoperável.`,
  pilares: [
    {
      nome: "Unidade Espacial (Spatial Unit)",
      descricao: "Delimitação geográfica da árvore ou do conjunto arbóreo com geometria precisa (ponto, polígono de copa, linha de eixo) e sistema de coordenadas definido.",
      icone: "ri-map-pin-line",
    },
    {
      nome: "Fonte (Source)",
      descricao: "Documentos, evidências, levantamentos, fotos e nuvem de pontos que comprovam a existência e características da árvore cadastrada.",
      icone: "ri-folder-info-line",
    },
    {
      nome: "Responsabilidade (RRR)",
      descricao: "Registro de Responsabilidades, Restrições e Direitos associados à árvore: quem deve manter, quem pode suprimir, quais restrições ambientais aplicam.",
      icone: "ri-shield-check-line",
    },
    {
      nome: "Parte (Party)",
      descricao: "Pessoas, instituições, empresas ou órgãos públicos vinculados à árvore ou ao projeto, com papéis definidos (proprietário, responsável, executor).",
      icone: "ri-team-line",
    },
    {
      nome: "Histórico (History)",
      descricao: "Versionamento completo das alterações: quando, quem e o que mudou em cada registro, permitindo auditoria e reconstituição temporal.",
      icone: "ri-history-line",
    },
    {
      nome: "Evento (Event)",
      descricao: "Registro de ocorrências ligadas à árvore: vistorias, manejos, supressões, plantios, doenças, quedas, com data, responsável e evidências.",
      icone: "ri-calendar-event-line",
    },
  ],
  fluxoEtapas: [
    "Planejamento da ação de cadastramento",
    "Levantamento base geoespacial",
    "Cadastro de campo (Coletor FloraIQ)",
    "Processamento 3D e extração de métricas",
    "Modelagem LADM-FloraIQ e vínculos",
    "Validação técnica e metodológica",
    "Publicação e gestão contínua",
  ],
};

export const memorialTechStack = {
  titulo: "Stack Tecnológico",
  categorias: [
    {
      categoria: "Frontend",
      itens: [
        { nome: "React 19", versao: "19.0.0", descricao: "Framework UI declarativo com concurrent features" },
        { nome: "TypeScript", versao: "5.7", descricao: "Tipagem estática para maior robustez" },
        { nome: "TailwindCSS", versao: "3.4", descricao: "Utility-first CSS framework" },
        { nome: "Vite", versao: "6.0", descricao: "Build tool com HMR ultrarrápido" },
        { nome: "React Router DOM", versao: "7.0", descricao: "Roteamento SPA" },
        { nome: "TanStack Query", versao: "5.62", descricao: "Gerenciamento de estado servidor" },
        { nome: "Zustand", versao: "5.0", descricao: "Gerenciamento de estado cliente leve" },
        { nome: "Recharts", versao: "2.15", descricao: "Gráficos e visualizações" },
      ],
    },
    {
      categoria: "Backend",
      itens: [
        { nome: "Python", versao: "3.12", descricao: "Linguagem principal do backend" },
        { nome: "FastAPI", versao: "0.115", descricao: "Framework web async de alto desempenho" },
        { nome: "Pydantic", versao: "2.10", descricao: "Validação e serialização de dados" },
        { nome: "SQLAlchemy", versao: "2.0", descricao: "ORM para PostgreSQL" },
        { nome: "Alembic", versao: "1.14", descricao: "Migrations de banco de dados" },
        { nome: "Celery", versao: "5.4", descricao: "Processamento de tarefas assíncronas" },
        { nome: "Redis", versao: "7.2", descricao: "Cache e broker de mensagens" },
        { nome: "Uvicorn", versao: "0.34", descricao: "Servidor ASGI" },
      ],
    },
    {
      categoria: "Banco de Dados e GIS",
      itens: [
        { nome: "PostgreSQL", versao: "16", descricao: "Banco relacional principal" },
        { nome: "PostGIS", versao: "3.4", descricao: "Extensão espacial para PostgreSQL" },
        { nome: "GeoAlchemy2", versao: "0.17", descricao: "ORM espacial para SQLAlchemy" },
        { nome: "Shapely", versao: "2.0", descricao: "Manipulação de geometrias" },
        { nome: "GeoJSON", versao: "RFC 7946", descricao: "Formato de intercâmbio espacial" },
        { nome: "GDAL/OGR", versao: "3.9", descricao: "Conversão entre formatos geoespaciais" },
      ],
    },
    {
      categoria: "Processamento 3D",
      itens: [
        { nome: "PDAL", versao: "2.8", descricao: "Processamento de nuvens de pontos" },
        { nome: "Potree", versao: "1.8", descricao: "Visualizador web de nuvens de pontos" },
        { nome: "CesiumJS", versao: "1.123", descricao: "Globo 3D e visualização geoespacial" },
        { nome: "Three.js", versao: "r170", descricao: "Renderização 3D web" },
        { nome: "Open3D", versao: "0.18", descricao: "Processamento 3D em Python" },
        { nome: "LAStools", versao: "2024", descricao: "Suite de processamento LiDAR" },
      ],
    },
    {
      categoria: "Infraestrutura",
      itens: [
        { nome: "Docker", versao: "27.0", descricao: "Containerização de serviços" },
        { nome: "Docker Compose", versao: "2.29", descricao: "Orquestração local" },
        { nome: "AWS / MinIO", versao: "Latest", descricao: "Armazenamento de objetos" },
        { nome: "Nginx", versao: "1.26", descricao: "Reverse proxy e CDN" },
        { nome: "Let's Encrypt", versao: "Latest", descricao: "Certificados SSL gratuitos" },
      ],
    },
  ],
};

export const memorialStandards = [
  {
    codigo: "ISO 19152:2012",
    titulo: "Land Administration Domain Model (LADM)",
    descricao: "Padrão internacional para modelagem de dados cadastrais e administrativos territoriais, base da metodologia FloraIQ-LADM.",
    status: "Adotado",
  },
  {
    codigo: "ISO 19136:2007",
    titulo: "Geography Markup Language (GML)",
    descricao: "Formato XML para codificação de dados geoespaciais, utilizado em exportações e interoperabilidade.",
    status: "Suportado",
  },
  {
    codigo: "ISO 6709:2008",
    titulo: "Representação de coordenadas geográficas",
    descricao: "Padronização da representação de latitude, longitude e altitude para referenciamento espacial.",
    status: "Adotado",
  },
  {
    codigo: "OGC WMS / WFS",
    titulo: "Web Map Service / Web Feature Service",
    descricao: "Protocolos OGC para serviços de mapas e features vetoriais via web, suportados nas exportações GIS.",
    status: "Suportado",
  },
  {
    codigo: "INCRA / SIGTAP",
    titulo: "Padrões federais de SIG",
    descricao: "Conformidade com os padrões do Sistema Nacional de Cadastro Rural e SIGTAP para interoperabilidade governamental.",
    status: "Em adaptação",
  },
  {
    codigo: "Lei nº 12.651/2012",
    titulo: "Código Florestal Brasileiro",
    descricao: "Compatibilidade com as normas de preservação, compensação e manejo de vegetação nativa em ambiente urbano.",
    status: "Referenciado",
  },
  {
    codigo: "Lei nº 14.285/2022",
    titulo: "Marco Legal das Startups",
    descricao: "Adequação às normas de contratação de soluções inovadoras por órgãos públicos.",
    status: "Referenciado",
  },
  {
    codigo: "LGPD (Lei nº 13.709/2018)",
    titulo: "Lei Geral de Proteção de Dados",
    descricao: "Conformidade com proteção de dados pessoais, consentimento, anonimização e direitos do titular.",
    status: "Adotado",
  },
];

export const memorialGlossary = [
  { termo: "CTA", definicao: "Cadastro Técnico Arbóreo — registro georreferenciado de cada árvore da cidade com dados botânicos, dendrométricos, localização, fotos, vistorias e manejo." },
  { termo: "FCA", definicao: "Ficha Cadastral Arbórea — documento técnico individual por árvore contendo todos os atributos cadastrais, de saúde, risco e recomendação." },
  { termo: "LADM", definicao: "Land Administration Domain Model — modelo conceitual ISO 19152 para organização de dados cadastrais territoriais com entidades Party, RRR, Spatial Unit, Source, BA Unit." },
  { termo: "RRR", definicao: "Responsibilities, Restrictions and Rights — responsabilidades, restrições e direitos vinculados a uma unidade espacial ou árvore." },
  { termo: "Spatial Unit", definicao: "Unidade Espacial — delimitação geográfica da árvore com geometria (ponto, polígono, linha), área e sistema de coordenadas." },
  { termo: "Party", definicao: "Parte Envolvida — pessoa, instituição, empresa ou órgão público com vínculo a uma árvore ou projeto (proprietário, responsável, executor)." },
  { termo: "Nuvem de Pontos", definicao: "Conjunto de coordenadas 3D (X, Y, Z) geradas por sensoriamento remoto (LiDAR, fotogrametria) representando a superfície do terreno e vegetação." },
  { termo: "Ortomosaico", definicao: "Imagem aérea composta por múltiplas fotos ortorretificadas, formando uma vista planimétrica da área de estudo." },
  { termo: "LiDAR", definicao: "Light Detection and Ranging — tecnologia de sensoriamento remoto que usa pulsos laser para medir distâncias e gerar nuvens de pontos 3D." },
  { termo: "SRID", definicao: "Spatial Reference System Identifier — código numérico que identifica o sistema de coordenadas geográficas utilizado (ex: 31983 para SIRGAS 2000 / UTM 23S)." },
  { termo: "Alometria", definicao: "Relação matemática entre dimensões corporais (diâmetro, altura) e biomassa/volume/carbono de uma árvore." },
  { termo: "Sequestro de Carbono", definicao: "Processo de captura e armazenamento de CO2 atmosférico na biomassa das árvores, quantificável em toneladas de carbono." },
  { termo: "Compensação Ambiental", definicao: "Obrigação legal de replantio ou preservação compensatória quando da supressão de vegetação nativa ou urbana." },
  { termo: "Vistoria Técnica", definicao: "Inspeção presencial de uma árvore por profissional habilitado para avaliação de risco, saúde, interferências e recomendação de manejo." },
  { termo: "Fitossanidade", definicao: "Condição de saúde vegetal de uma árvore, considerando pragas, doenças, danos mecânicos e estresse ambiental." },
  { termo: "Interferência", definicao: "Situação em que a árvore ou sua copa entram em conflito com infraestrutura urbana (fiação, postes, edificações, passeios)." },
  { termo: "Supressão", definicao: "Remoção total ou parcial de uma árvore, geralmente sujeita a autorização prévia do órgão ambiental competente." },
  { termo: "Poda", definicao: "Intervenção cirúrgica em copa, raízes ou tronco com objetivo de segurança, saúde ou adequação urbana." },
  { termo: "UC", definicao: "Unidade de Conservação — área legalmente protegida com objetivos de conservação da natureza (Parque Natural, APA, RPPN)." },
  { termo: "GeoJSON", definicao: "Formato aberto para codificação de estruturas de dados geoespaciais usando a notação de objetos JavaScript (JSON)." },
  { termo: "PWA", definicao: "Progressive Web App — aplicação web que funciona como app nativo, incluindo suporte offline, push notifications e instalação." },
  { termo: "DAP", definicao: "Diâmetro à Altura do Peito — medida padronizada do tronco a 1,30m do solo, principal indicador dendrométrico." },
  { termo: "Copa", definicao: "Parte superior da árvore composta por galhos, ramos e folhagem, com formato e dimensões variáveis." },
  { termo: "Fuste", definicao: "Parte do tronco entre o solo e o início da copa, principal suporte estrutural da árvore." },
];

export const memorialReleases = [
  {
    versao: "v1.0.0",
    data: "01/07/2026",
    status: "Em desenvolvimento",
    milestones: [
      "Módulo CTA Arbóreo com FCA completa",
      "Dashboard operacional com KPIs",
      "Mapa interativo com camadas temáticas",
      "Gestão de projetos e cronogramas",
      "Módulo Vistorias com ciclos de monitoramento",
      "Módulo Ambiental (áreas verdes e UCs)",
      "Módulo Carbono com modelos alométricos",
      "Documentos e Fontes com rastreabilidade",
      "Partes Envolvidas e RRR",
      "Metodologia LADM-FloraIQ",
      "Nuvem de Pontos 3D (simulado)",
      "Validação e Qualidade (32 regras)",
      "Fluxo Operacional (7 etapas)",
      "Relatórios técnicos e executivos",
      "Configurações e auditoria",
      "Coletor de Campo PWA (mock)",
      "Portal Público (mock)",
    ],
  },
  {
    versao: "v1.1.0",
    data: "Previsto Q4 2026",
    status: "Planejado",
    milestones: [
      "Integração com backend Python/FastAPI real",
      "Supabase para auth, banco e storage",
      "Processamento real de nuvens de pontos (PDAL)",
      "Coletor de Campo PWA funcional offline",
      "Integração com sistemas municipais (SIGM, SIGTAP)",
      "API pública para integrações externas",
    ],
  },
  {
    versao: "v2.0.0",
    data: "Previsto 2027",
    status: "Planejado",
    milestones: [
      "Inteligência artificial para identificação de espécies",
      "Predição de risco baseada em dados históricos",
      "Dashboard municipal integrado",
      "App nativo iOS/Android",
      "Integração com IoT (sensores de umidade, vento)",
      "Marketplace de serviços ambientais",
    ],
  },
];